import { MouseEventHandler, MouseEventParams, Time } from "lightweight-charts";
import cloneDeep from 'lodash/cloneDeep'
import { ChartState, IChart } from "../../../types/chart.interface";
import { ChartCandle, ChartLayoutOptions } from "../chartV2/chart.typings";
import { SuperChartConfiguration_TimeBucket } from "../../proto/generated/superchart-page";
import { getTimeBucketsGCD } from "../../util/timeBuckets";
import { TimeBucket, TimeBucket_TimeSeries } from "../../proto/generated/common";
import { Security } from "../../proto/generated/security";
import { getCandleRequestV2 } from "../chartV2/chartHelpers";
import { GetCandlesRequestV2 } from "../../proto/generated/candle-data-depot";
import { istToUTC } from "../../util/dateTime";
import { getCandlesBulkV2 } from "../../services/apis/candles";
import { getSecurityKey } from "../../util/security";
import { prepareSeriesData } from "../../util/chart";
import { DrawingDisplayType } from "../chartV2/drawingTools/drawing.interface";


export type ReplayManagerCallbacks = {
    onReplayTimeSelection?: (maxAvailableTimeBucket: TimeBucket, time: number) => void
    onReplayComplete?: () => void
}

export type ReplayManagerOptions = {
    callbacks: ReplayManagerCallbacks
}

class ReplayManager {
    charts: Record<string, IChart> = {};
    layoutOptions: Record<string, ChartLayoutOptions> = {};
    clickCallbacks: Record<string, MouseEventHandler<Time>> = {};
    timeBuckets: SuperChartConfiguration_TimeBucket[] = [];
    callbacks: ReplayManagerCallbacks = {};
    maxAvailableTimeBucket?: TimeBucket;
    replayTimeBucket?: TimeBucket;
    replayStartTime?: number
    data: Record<string, ChartCandle[]> = {};
    currentReplayTime?: number
    currentReplayIndex: Record<string, number> = {};
    ltp: Record<string, number> = {}
    playTimeoutId?: number;
    playInterval = 1000;
    chartStates: Record<string, ChartState> = {};

    constructor(options: ReplayManagerOptions) {
        if (options.callbacks) {
            this.callbacks = {
                ...options.callbacks
            }
        }
    }

    async initialize(charts: Array<IChart>) {
        this.charts = charts.reduce((acc, c) => {
            return {
                ...acc,
                [c.getChartId()]: c
            }
        }, {} as Record<string, IChart>);
        this.layoutOptions = {};
        this.clickCallbacks = {};
    }
    async enableReplayMode() {
        await Promise.all(Object.values(this.charts).map(async (c) => {
            const chartId = c.getChartId();
            await c.enableReplayMode();
            this.chartStates[chartId] = c.getState()
            this.layoutOptions[chartId] = {crosshair: cloneDeep(c.getCurrentLayoutOptions().crosshair)};
            c.updateLayoutOptions({
                crosshair: {
                    vertLine: {
                        visible: false,
                        labelVisible: false
                    },
                    horzLine: {
                        visible: false,
                        labelVisible: false
                    }
                }
            })


            const primaryPaneId = c.getPrimaryPaneId();
            const primarySeriesId = c.getPrimarySeriesId();
            if (!primaryPaneId || !primarySeriesId) return;
            c.addDrawing('replaySelection', DrawingDisplayType.TOOL, { persist: false }, {}, "replaySelection", {time: 0}, primaryPaneId, primarySeriesId)
            c.updateCallbackListeners({
                onReplaySelectionMove: this.onReplaySelectionMove
            })

            const clickCallback = this.onReplayTimeSelect.bind(this, chartId)
            this.clickCallbacks[chartId] = clickCallback;
            const api = c.getChartApi();
            api.subscribeClick(clickCallback)
        }))
    }

    async onReplayTimeSelect(chartId: string, params: MouseEventParams) {
        const chart = this.charts[chartId];
        const time = chart.getCurrentReplaySelectionTime();
        this.replayStartTime = time;
        const allCharts = Object.values(this.charts)
        const timeBuckets: SuperChartConfiguration_TimeBucket[] = [];
        allCharts.forEach(c => {
            timeBuckets.push(c.getTimeBucket())
            c.removeDrawing('replaySelection')
        })
        const maxAvailableTimeBucket = getTimeBucketsGCD(timeBuckets);
        if (maxAvailableTimeBucket.timeSeries !== TimeBucket_TimeSeries.TIME_SERIES_UNKNOWN) {
            this.maxAvailableTimeBucket = maxAvailableTimeBucket
            this.replayTimeBucket = maxAvailableTimeBucket;

            this.callbacks.onReplayTimeSelection && this.callbacks.onReplayTimeSelection(maxAvailableTimeBucket, time);

            await Promise.all(allCharts.map(async (ch) => {
                await ch.initializeReplayMode(time);
            }))

            await this.fetchForwardData(this.replayStartTime);

            allCharts.forEach((ch) => {
                const id = ch.getChartId();
                const clickCallback = this.clickCallbacks[id];
                if (clickCallback) {
                    const api = ch.getChartApi();
                    api.unsubscribeClick(clickCallback);
                    delete this.clickCallbacks[id];
                }
                if (this.layoutOptions[id]) {
                    ch.getChartApi().applyOptions(this.layoutOptions[id]);
                }
            });
        }
    }

    async fetchForwardData(date: number) {
        if (!this.replayTimeBucket) return;
        const allCharts = Object.values(this.charts);
        const superChartTimeBucket: SuperChartConfiguration_TimeBucket = {
            code: '',
            name: '',
            timeSeries: this.replayTimeBucket.timeSeries,
            timeValue: this.replayTimeBucket.value
        }
        const securities: Security[] = [];
        const tradingCalendarData = allCharts[0].getCalendarData()
        if (!tradingCalendarData) return;
        const forwardCandleRequests: GetCandlesRequestV2[] = []
        allCharts.forEach(c => {
            securities.push(...c.getAllSecurities())
        })

        securities.forEach((sec) => {
            forwardCandleRequests.push(getCandleRequestV2(
                0,
                500,
                sec,
                superChartTimeBucket,
                istToUTC(date)*1000,
                false,
                true
            ))
        })

        const candles = await getCandlesBulkV2(forwardCandleRequests);

        candles.responses.forEach(response => {
            if (!response.security) return
            const key = getSecurityKey(response.security.exchange, response.security.code)
            this.currentReplayIndex[key] = -1;
            this.data[key] = prepareSeriesData({candles: response.forwardCandles, priceMultiplier: response.security.priceMultiplier ?? 100, timezone: allCharts[0].getTimeZone(), lastTickTimestamp: response.lastCandleStoreUpdateTime, fillEmptySlots: true, timeBucket: superChartTimeBucket, calendarData: tradingCalendarData, security: response.security})
        });
        
    }

    onReplaySelectionMove = (time: number, price: number) => {
        Object.values(this.charts).map((c) => {
            c.setReplaySelectionTime(time, price)
        })
    }

    async disableReplayMode() {
        this.stop();
        await Promise.all(Object.values(this.charts).map(async (c) => {
            const id = c.getChartId();
            c.updateLayoutOptions(this.layoutOptions[id])
            c.removeCallbackListeners({
                onReplaySelectionMove: this.onReplaySelectionMove
            })
            delete this.layoutOptions[id];
            c.removeDrawing("replaySelection");
            c.setState(this.chartStates[id])
            await c.disableReplayMode();
            const clickCallback = this.clickCallbacks[id];
            if (clickCallback) {
                const api = c.getChartApi();
                api.unsubscribeClick(clickCallback)
                delete this.clickCallbacks[id]
            }
        }));
        this.reset();
    }

    updateCallbacks(callbacks: Partial<ReplayManagerCallbacks>) {
        this.callbacks = {
            ...this.callbacks,
            ...callbacks
        }
    }
    play = () => {
        this.pushNextCandle();
    }
    gotoNextCandle = () => {
        this.pushNextCandle(true);
    }

    stop = () => {
        clearInterval(this.playTimeoutId)
        this.playTimeoutId = undefined;
    }

    pushNextCandle = (manual = false) => {
        const nextCandleTimes: number[] = []
        const securities = Object.keys(this.data)
        const indexes = this.currentReplayIndex;
        securities.forEach((key) => {
            if (indexes[key] === undefined || !this.data[key][indexes[key] + 1]) return;
            nextCandleTimes.push(this.data[key][indexes[key] + 1].time as number)
        })

        if (nextCandleTimes.length === 0) {
            this.stop();
            this.callbacks.onReplayComplete?.();
            return;
        }

        const nextCandleTime = Math.min(...nextCandleTimes)
        const nextCandleData: Record<string, ChartCandle> = {};
        nextCandleTimes.forEach((cT, i) => {
            if (cT === nextCandleTime) {
                const key = securities[i];
                indexes[key]++;
                nextCandleData[key] = this.data[key][indexes[key]]
            }
        })

        const allCharts = Object.values(this.charts);

        allCharts.forEach((ch) => {
            ch.pushReplayData(nextCandleData)
        })

        if (!manual) {
            this.playTimeoutId = window.setTimeout(this.pushNextCandle, this.playInterval)
        }
    }

    setReplaySpeed(speed: number) {
        this.playInterval = Math.floor(1000/speed);
        if (this.playTimeoutId) {
            this.stop();
            this.play();
        }
    }

    reset() {
        this.data = {};
        this.ltp = {};
        this.currentReplayIndex = {};
        this.charts = {};
        this.chartStates = {};
        this.layoutOptions = {};
        this.clickCallbacks = {};
        this.timeBuckets = [];
        this.maxAvailableTimeBucket = undefined;
        this.replayTimeBucket = undefined;
        this.replayStartTime = undefined
        this.currentReplayTime = undefined;
        this.playTimeoutId = undefined
        this.playInterval = 1000;
    }

}

export default ReplayManager;